import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

export type IconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  label: string;
  children: ReactNode;
  tone?: "default" | "quiet" | "danger";
};

/** A consistently sized icon-only button with its required accessible name. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { label, children, tone = "default", className, type = "button", ...props },
    ref
  ) {
    const tones = {
      default:
        "border border-border/70 bg-surface-raised text-foreground hover:bg-surface-muted",
      quiet:
        "bg-transparent text-foreground-muted hover:bg-surface-muted hover:text-foreground",
      danger: "bg-danger/10 text-danger hover:bg-danger/15",
    } as const;

    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        className={cn(
          "inline-flex min-h-11 min-w-11 items-center justify-center rounded-full",
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "disabled:pointer-events-none disabled:opacity-50",
          tones[tone],
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
